import { useState, useEffect } from 'react';
import {
  Menu,
  Button,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { ArtistModel } from 'models/Artist/Artist';
import BasicArtistCard from '../BasicArtistCard/BasicArtistCard';

export interface ArtistDropdownProps {
  artists: ArtistModel[];
  onArtistSelect?: (artists: ArtistModel[]) => void;
  multiSelectable?: boolean;
  defaultSelectedArtists?: ArtistModel[];
  maxWidth?: boolean;
}

export default function ArtistDropdown({ artists, onArtistSelect, defaultSelectedArtists = [], multiSelectable = false, maxWidth = false }: ArtistDropdownProps) {
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>(defaultSelectedArtists);
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const highlightBorder = useColorModeValue('blue.500', 'blue.300');

  useEffect(() => {
    setSelectedArtists(defaultSelectedArtists);
  }, [defaultSelectedArtists]);

  const handleArtistClick = (artist: ArtistModel) => {
    let newSelectedArtists: ArtistModel[];

    if (multiSelectable) {
      const isSelected = selectedArtists.some(a => a.id === artist.id);
      if (isSelected) {
        newSelectedArtists = selectedArtists.filter(a => a.id !== artist.id);
      } else {
        newSelectedArtists = [...selectedArtists, artist];
      }
    } else {
      const isCurrentlySelected = selectedArtists.some(a => a.id === artist.id);
      newSelectedArtists = isCurrentlySelected ? [] : [artist];
    }

    setSelectedArtists(newSelectedArtists);
    onArtistSelect?.(newSelectedArtists);
  };

  const isArtistSelected = (artistId: string): boolean => {
    return selectedArtists.some(a => a.id === artistId);
  };

  const menuContent = artists.map((artist, index) => {
    const isSelected = isArtistSelected(artist.id);
    return (
      <MenuItem
        key={`artistDropdown_artist_${index}`}
        padding={'0px'}
        px={2}
        onClick={() => handleArtistClick(artist)}
        backgroundColor={isSelected ? highlightBg : 'transparent'}
        borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
        borderColor={isSelected ? highlightBorder : 'transparent'}
        _hover={{ backgroundColor: isSelected ? highlightBg : 'gray.100' }}
      >
        <BasicArtistCard
          avatarSize='sm'
          titleSize='sm'
          artist={artist}
          showBorder={false}
        />
      </MenuItem>
    );
  });

  function renderMenuButton() {
    if (multiSelectable) {
      if (selectedArtists.length > 0) {
        return (
          <Text fontSize="sm" color="gray.300">
            {`${selectedArtists.length} Artists Selected`}
          </Text>
        );
      } else {
        return <Text fontSize="sm" color="gray.300">Select Artists</Text>;
      }
    } else if (selectedArtists.length > 0) {
      return (
        <BasicArtistCard
          avatarSize='sm'
          titleSize='sm'
          artist={selectedArtists[0]}
          showBorder={false}
        />
      );
    } else {
      return <Text fontSize="sm" color="gray.300">Select An Artist</Text>;
    }
  }

  return (
    <Menu closeOnSelect={!multiSelectable}>
      <MenuButton
        borderRadius='8px'
        variant='outline'
        textAlign='left'
        width={maxWidth ? "100%" : "auto"}
        as={Button}
        rightIcon={<ChevronDownIcon />}
      >
        {renderMenuButton()}
      </MenuButton>
      <MenuList width="100%" maxHeight="300px" overflow="scroll" backgroundColor={'white'} zIndex='100'>
        {menuContent}
      </MenuList>
    </Menu>
  );
}
